/**
 * `DefenceSplashScreen` — GVW-168 standalone state page.
 *
 * Defence variant indicator splash modal. Verbatim disclosure required.
 * Dismissible per session via viewer store action.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-168/brief.md
 * Copy: copy-deck.md gvw.168.* (verbatim disclosure)
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';
export function DefenceSplashScreen() {
  const liveRegion = useLiveRegion();
  const navigate = useNavigate();

  useEffect(() => {
    // Copy key: gvw.168.title
    document.title = 'Local-only mode · GeoViewer';
    // Status role per brief.md §7
    liveRegion.announce('status', 'Local-only mode active');
  }, [liveRegion]);

  const handleDismiss = () => {
    // Per brief.md §7.h - navigate to workspace (store action not present)
    navigate('/workspace');
  };

  return (
    <main
      role="main"
      aria-labelledby="defence-splash-heading"
      data-gvw-id="GVW-168"
      data-route="/defence-splash"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.overlay.scrim,
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      {/* Modal dialog per brief.md §8 accessibility plan */}
      <div
        role="dialog"
        aria-modal="true"
        aria-describedby="defence-disclosure"
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.info}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
          boxShadow: tokens.elevation[3],
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.semantic.info,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          policy
        </div>

        {/* Copy key: gvw.168.title from copy-deck.md */}
        <h1
          id="defence-splash-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Local-only mode
        </h1>

        {/* Verbatim disclosure per copy-deck.md gvw.168.body + brief.md §16 */}
        <p
          id="defence-disclosure"
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '54ch',
          }}
        >
          Local-only mode active. No external services contacted. Audit retention is at least 365 days.
        </p>

        {/* Additional security indicators per brief.md icon plan */}
        <div
          style={{
            display: 'flex',
            gap: tokens.space[4],
            alignItems: 'center',
            fontSize: tokens.font.size.sm,
            color: tokens.color.ink.muted,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[1] }}>
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 16,
              }}
            >
              shield
            </span>
            Secured
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[1] }}>
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 16,
              }}
            >
              cloud_off
            </span>
            No external calls
          </div>
        </div>

        {/* Dismissal action per brief.md §10 */}
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
            marginTop: tokens.space[2],
          }}
        >
          Continue to workspace
        </button>
      </div>
    </main>
  );
}